/**
 * CambiarClaveDialog — el usuario cambia su PROPIA clave verificando la actual.
 *
 * Se abre desde la tarjeta del usuario en el menú lateral (cualquier usuario,
 * con o sin acceso a Configuración) y desde Usuarios y roles. El endpoint
 * /api/config_cambiarclave no está en RUTAS_PROTEGIDAS: solo exige la clave
 * vigente.
 *
 * Al guardar se actualiza la clave de la sesión en memoria (claveLog): el
 * backend resuelve el tenant con usuario + clave en cada llamada, así que con
 * la vieja todo lo siguiente fallaría con "Usuario o clave invalidos".
 */
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import SaveIcon from "@mui/icons-material/Save";
import { useGlobalMDAlert, useMaterialUIController, setClaveLog } from "context";
import { FP, fpInputSx } from "components/financepro";
import { useApi } from "lib/api";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const VACIO = { actual: "", nueva: "", confirmacion: "" };

export default function CambiarClaveDialog({ open, onClose }) {
  const [, dispatch] = useMaterialUIController();
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [form, setForm] = useState(VACIO);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (open) setForm(VACIO);
  }, [open]);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const guardar = async () => {
    if (form.nueva.length < 5) {
      showAlert({ message: "La clave nueva debe tener al menos 5 caracteres", color: "warning" });
      return;
    }
    if (form.nueva !== form.confirmacion) {
      showAlert({ message: "La confirmación no coincide con la clave nueva", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/config_cambiarclave", {
        claveactual: form.actual,
        clavenueva: form.nueva,
      });
      if (ok) {
        setClaveLog(dispatch, form.nueva);
        onClose();
        showAlert({ message: "Clave actualizada", color: "success" });
      } else {
        showAlert({ message: data.Error || "No se pudo cambiar la clave", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>Cambiar mi clave</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          spacing={2}
          sx={{ mt: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            guardar();
          }}
        >
          <TextField
            fullWidth
            autoFocus
            type="password"
            label="Clave actual"
            autoComplete="current-password"
            value={form.actual}
            onChange={setCampo("actual")}
            sx={fpInputSx}
          />
          <TextField
            fullWidth
            type="password"
            label="Clave nueva"
            autoComplete="new-password"
            value={form.nueva}
            onChange={setCampo("nueva")}
            helperText="Mínimo 5 caracteres"
            sx={fpInputSx}
          />
          <TextField
            fullWidth
            type="password"
            label="Confirmar clave nueva"
            autoComplete="new-password"
            value={form.confirmacion}
            onChange={setCampo("confirmacion")}
            sx={fpInputSx}
          />
          {/* Enter en cualquier campo guarda */}
          <button type="submit" hidden aria-hidden="true" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={guardando}
          sx={{
            ...btnBase,
            color: FP.color.textSecondary,
            "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
          }}
        >
          Cancelar
        </Button>
        <Button
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={guardar}
          disabled={guardando}
          sx={{
            ...btnBase,
            bgcolor: FP.color.navy,
            color: "#FFFFFF",
            "&:hover": { bgcolor: FP.color.navySoft, boxShadow: "none", color: "#FFFFFF" },
            "&.Mui-disabled": { bgcolor: `${FP.color.navy}66`, color: "#FFFFFF" },
          }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
